import React, { useEffect, useRef } from 'react';
import { message, notification, Button, ConfigProvider } from '@app/ui';

import connect from '@app/utils/connect';
import notificationActions from '@app/actions/notifications';

import { useAlert } from './useAlert';

import './notification.scss';

export type Notification = {
    id: string;
    text: string;
    shouldAutoClose?: boolean;
    hasActionButton?: boolean;
    options?: {
      description?: string | React.ReactNode;
        context?: 'success' | 'info' | 'warning' | 'error' | 'danger';
        type?: 'message' | 'alert' | 'notification';
        onClickActionButton?: () => void;
        onCancelActionButton?: () => void;
    };
};

type NotificationsProps = {
    notifications: Notification[];
    clear: (id: Notification['id']) => void;
};

function Notifications({ notifications = [], clear }: NotificationsProps) {
    const [messageApi, messageContextHolder] = message.useMessage();
    const [notificationApi, notificationContextHolder] =
        notification.useNotification();
    const { alertApi, alertContextHolder } = useAlert(clear);

    // Store clear function in ref to avoid dependency issues
    const clearRef = useRef(clear);
    clearRef.current = clear;

    // Store processed notifications to avoid reprocessing
    const processedNotifications = useRef<Set<string>>(new Set());

    useEffect(() => {
        if (notifications.length === 0) {
            alertApi.destroyAll();
            processedNotifications.current.clear();
            return;
        }

        notifications.forEach(notification => {
            // Skip if already processed
            if (processedNotifications.current.has(notification.id)) {
                return;
            }

            const {
                text,
                id,
                options,
                shouldAutoClose = true,
                hasActionButton = false,
            } = notification;

            const {
                type = 'alert',
                description,
                onClickActionButton,
                onCancelActionButton,
            } = options || {};

            const context =
                options?.context === 'danger' ? 'error' : options?.context;

            switch (type) {
                case 'alert': {
                    alertApi.open(id, {
                        ...notification,
                        shouldAutoClose,
                        options: {
                            ...options,
                            type,
                            context: context || 'success',
                        },
                    });
                    break;
                }
                case 'message': {
                    messageApi.open({
                        type: context || 'success',
                        content: text,
                        duration: 10,
                        onClose: () => clearRef.current(id),
                    });
                    break;
                }
                case 'notification': {
                    const notificationConfig: any = {
                        message: text,
                        key: id,
                        description: description,
                        duration: shouldAutoClose ? 10 : null,
                        onClose: () => {
                            onCancelActionButton?.();
                            notificationApi.destroy(id);
                            clearRef.current(id);
                        },
                        closable: true,
                    };

                    const buttons = [];

                    if (onCancelActionButton) {
                        buttons.push(
                            <Button
                                type="link"
                                size="small"
                                onClick={() => {
                                    onCancelActionButton?.();
                                    notificationApi.destroy(id);
                                    clearRef.current(id);
                                }}
                            >
                                Cancel
                            </Button>,
                        );
                    }

                    if (onClickActionButton) {
                        buttons.push(
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => {
                                    onClickActionButton();
                                    notificationApi.destroy(id);
                                    clearRef.current(id);
                                }}
                            >
                                Continue
                            </Button>,
                        );
                    }

                    if (hasActionButton) {
                        notificationConfig.btn = (
                            <div
                                style={{
                                    marginTop: 8,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                {buttons}
                            </div>
                        );
                    }

                    if (context) {
                        notificationApi[context](notificationConfig);
                    } else {
                        notificationApi.open(notificationConfig);
                    }
                    break;
                }
                default: {
                    return null;
                }
            }

            // Mark as processed
            processedNotifications.current.add(id);
        });
    }, [notifications]);

    return (
        <ConfigProvider
            theme={{
                components: {
                    Notification: {
                        fontFamily: 'Roboto',
                        colorText: 'rgba(0, 0, 0, 0.88)',
                        fontSize: 14,
                    },
                    Alert: {
                        fontFamily: 'Roboto',
                        colorText: 'rgba(0, 0, 0, 0.88)',
                        fontSize: 14,
                    },
                    Message: {
                        fontFamily: 'Roboto',
                        colorText: 'rgba(0, 0, 0, 0.88)',
                        fontSize: 14,
                    },
                },
            }}
        >
            {notifications.length > 0 && messageContextHolder}
            {notifications.length > 0 && notificationContextHolder}
            {notifications.length > 0 && alertContextHolder}
        </ConfigProvider>
    );
}

export default connect(['notifications'], notificationActions)(Notifications);
